import React, { useState } from 'react';
import axios from 'axios';
import { MessageSquarePlus, X, Send, Bug, Lightbulb, Heart, Check } from 'lucide-react';

// Floating feedback button. Persists each note to the backend `feedback`
// collection (admin inbox + CSV export) and fires a best-effort email ping to
// the founder. Replaced the old mailto-only flow so beta feedback can be
// tracked at volume rather than getting lost in an inbox.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'bug', label: 'Bug', icon: Bug, color: 'enf-role-warning' },
  { id: 'idea', label: 'Idea', icon: Lightbulb, color: 'enf-role-info' },
  { id: 'love', label: 'Love it', icon: Heart, color: 'enf-accent' },
];

const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [page] = useState(() => (typeof window !== 'undefined' ? window.location.pathname : '/'));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/feedback`, {
        category,
        message: message.trim(),
        page,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
      setSent(true);
      setMessage('');
      setTimeout(() => {
        setSent(false);
        setOpen(false);
      }, 1600);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 401
          ? 'Please sign in to send feedback.'
          : "Couldn't send just now — try again in a moment.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-40 group flex items-center gap-2 pl-3 pr-4 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-full shadow-xl shadow-enf-accent/30 transition-all hover:scale-105"
          data-testid="feedback-widget-trigger"
          aria-label="Send feedback"
        >
          <MessageSquarePlus className="w-4 h-4 text-enf-text-primary" />
          <span className="text-xs font-semibold uppercase tracking-widest text-enf-text-primary hidden sm:inline">Feedback</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 left-6 z-40 w-[360px] max-w-[calc(100vw-2rem)] bg-enf-surface-deep border border-enf-border-special/40 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="feedback-widget-panel">
          {/* Header */}
          <div className="px-4 py-3 border-b border-enf-surface-border flex items-center justify-between bg-gradient-to-r from-enf-surface-special/30 to-transparent">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-enf-role-info font-bold">Private Beta</div>
              <div className="font-display text-xl tracking-wide leading-none mt-0.5">Tell me anything.</div>
            </div>
            <button onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-md bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-secondary hover:text-enf-text-primary flex items-center justify-center transition-colors"
              data-testid="feedback-widget-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4">
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">What's this about?</div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.id;
                return (
                  <button key={c.id} onClick={() => setCategory(c.id)}
                    className={`py-2 rounded-md border text-xs font-semibold transition-colors flex flex-col items-center gap-1 ${
                      active ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
                    }`}
                    data-testid={`feedback-category-${c.id}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {c.label}
                  </button>
                );
              })}
            </div>

            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Your note</div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What worked, what broke, what you wish was different..."
              rows={4}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm placeholder:text-enf-text-muted focus:outline-none focus:border-enf-accent-fg resize-none"
              data-testid="feedback-widget-textarea"
            />

            <button
              onClick={send}
              disabled={!message.trim() || sending || sent}
              className="w-full mt-3 py-2.5 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-mid disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="feedback-widget-send"
            >
              {sent ? (
                <><Check className="w-3.5 h-3.5" /> Got it — thank you</>
              ) : (
                <><Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send to Ironbridge'}</>
              )}
            </button>

            {error && (
              <div className="text-[11px] text-enf-role-danger text-center mt-2" data-testid="feedback-widget-error">
                {error}
              </div>
            )}

            <div className="text-[10px] text-enf-text-muted text-center mt-2 leading-relaxed">
              Goes straight to me — I read every one, usually within a day.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
